<script setup lang="ts">
import { computed } from 'vue'
import type { DeploymentMaintenanceState } from '~/composables/api/useDeploymentMaintenance'

type Props = {
  state: DeploymentMaintenanceState
}

const props = defineProps<Props>()

const isRecette = computed(() => props.state.deployEnv === 'dev')

const activeComponents = computed(() =>
  (props.state.components ?? []).filter((bucket) => bucket.active).map((bucket) => bucket.component)
)

const componentLabel = computed(() => {
  const components = activeComponents.value
  if (components.length === 2) return 'le backend (API) et l’interface (IHM)'
  if (components[0] === 'api') return 'le backend (API)'
  if (components[0] === 'ihm') return "l’interface (IHM)"
  return "l’application"
})

const estimatedMinutes = computed(() => {
  const secondsCandidates = (props.state.components ?? [])
    .filter((bucket) => bucket.active)
    .map((bucket) => bucket.estimatedRemainingSeconds)
    .filter((value): value is number => value !== null && Number.isFinite(value) && value >= 0)
  if (!secondsCandidates.length) return null
  const seconds = Math.max(...secondsCandidates)
  return Math.max(1, Math.round(seconds / 60))
})
</script>

<template>
  <div v-if="state.active" class="maintenance-banner" role="status" aria-live="polite">
    <div class="maintenance-banner__inner">
      <span class="maintenance-banner__icon material-symbols-rounded" aria-hidden="true">build_circle</span>
      <div class="maintenance-banner__content">
        <p class="maintenance-banner__title">
          Perturbation en cours : le serveur n'est pas accessible et Mon Orga fonctionne uniquement en mode lecture.
        </p>
        <p v-if="isRecette" class="maintenance-banner__detail">
          En recette, mise à jour de {{ componentLabel }}.
        </p>
        <p v-if="estimatedMinutes !== null" class="maintenance-banner__eta">
          Temps estimé : {{ estimatedMinutes }} min (durée indicative, susceptible de varier).
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/index" as *;

.maintenance-banner {
  width: 100%;
  position: sticky;
  top: 0;
  z-index: 1500;
  border-bottom: 1px solid var(--color-border);
  background: var(--color-surface-feedback-warning-light);
  color: var(--color-on-surface);
}

.maintenance-banner__inner {
  display: flex;
  align-items: flex-start;
  gap: $spacing-s;
  padding: $spacing-s $spacing-xl;
}

.maintenance-banner__icon {
  font-size: 22px;
  margin-top: 1px;
}

.maintenance-banner__content {
  min-width: 0;
}

.maintenance-banner__title {
  margin: 0;
  font-weight: 600;
}

.maintenance-banner__detail,
.maintenance-banner__eta {
  margin: 2px 0 0 0;
  font-size: 0.95rem;
}
</style>

